import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const ListarGrupos = ({ refresh }) => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🗑️ Eliminar grupo (memoizado)
  const deleteGrupo = useCallback(async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/grupos/${id}`);

        Swal.fire({
          icon: "success",
          title: data.message,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error al eliminar el grupo:", error.message);
      Swal.fire("Error", "No se pudo eliminar el grupo.", "error");
    }
  }, []);

  // 📥 Obtener datos
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/grupos/listall/");
      const lista = data?.grupos || [];

      const gruposConFunciones = lista
        .map((grupo) => ({
          ...grupo,

          // 📚 Programa (control ancho)
          nombrePrograma: (
            <div
              title={grupo.programaObjectId?.nombrePrograma || ""}
            >
              {grupo.programaObjectId?.nombrePrograma || "Programa no disponible"}
            </div>
          ),

          // 🏫 Centro (control ancho) - por si lo habilitas luego
          nombreCentro: (
            <div
              title={grupo.centroObjectId?.nombreCentro || ""}
            >
              {grupo.centroObjectId?.nombreCentro || "Centro no disponible"}
            </div>
          ),

          // 🌎 Ciudad (control ancho)
          nombreCiudad: (
            <div
              title={grupo.ciudadObjectId?.nombreCiudad || ""}
            >
              {grupo.ciudadObjectId?.nombreCiudad || "Ciudad no disponible"}
            </div>
          ),

          // 📅 Fechas formateadas
          finiciolectivaGrupo: grupo.finiciolectivaGrupo
            ? new Date(grupo.finiciolectivaGrupo).toLocaleDateString("es-CO")
            : "",
          ffinlectivaGrupo: grupo.ffinlectivaGrupo
            ? new Date(grupo.ffinlectivaGrupo).toLocaleDateString("es-CO")
            : "",

          // ⚙️ Acciones (nuevo diseño)
          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">


              {/* Registrar Acta 
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/institucional/actas/nuevo/${grupo._id}/${grupo.fichaGrupo}`
                  )
                }
                title="Registrar Acta"
                type="button"
              >
                <i className="fa-solid fa-file-circle-plus"></i>
              </button>
              */}

              {/* Listar Actas */}
              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(
                    `/institucional/actas/${grupo.fichaGrupo}`
                  )
                }
                title="Listar Actas del Grupo"
                type="button"
              >
                <i className="fa-solid fa-list-check"></i>
              </button>

              {/* Editar */}
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/institucional/grupos/edit/${grupo._id}`)}
                title="Editar Grupo"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              {/* Eliminar */}
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={async () => {
                  await deleteGrupo(grupo._id);
                  getData();
                }}
                title="Eliminar Grupo"
                type="button"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        }))
        .reverse();

      setGrupos(gruposConFunciones);
    } catch (error) {
      console.error("Error al cargar los grupos:", error.message);
      Swal.fire("Error", "No se pudo cargar la lista de grupos.", "error");
    } finally {
      setLoading(false);
    }
  }, [deleteGrupo, navigate]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // 📊 Columnas
  const columns = useMemo(
    () => [
      { Header: "Ficha", accessor: "fichaGrupo" },
      {Header: "Programa", accessor: "nombrePrograma"},
      { Header: "Centro", accessor: "nombreCentro", width: 300 }, // si lo quieres mostrar, descomenta
      { Header: "F.I Lectiva", accessor: "finiciolectivaGrupo" },
      { Header: "F.F Lectiva", accessor: "ffinlectivaGrupo" },
      { Header: "Ciudad", accessor: "nombreCiudad", width: 240 },
      { Header: "Estado", accessor: "estadoGrupo" },
      { accessor: "funciones" },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Grupos de Formación</h5>
                <small className="text-secondary">
                  Gestión institucional de grupos y actas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                      } me-2`}
                  ></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/institucional/grupos/nuevo")}
                  title="Crear Grupo"
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" />
                <div className="mt-3">Cargando grupos…</div>
              </div>
            ) : Array.isArray(grupos) && grupos.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={grupos} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👥</div>
                <div className="fw-semibold">No hay grupos registrados</div>
                <div className="small mb-3">
                  Crea el primer grupo para iniciar la gestión.
                </div>
                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate("/institucional/grupos")}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Crear grupo
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGrupos;